import { TemplateType } from 'constants/templateTypes';
import { Locale } from 'modules/domains/template';
import { MessageMedium } from 'modules/graphql/types/GraphQLMessageMedium';
import { OffsetDateTime } from 'modules/graphql/types/OffsetDateTime';
import { GqlInvalidContentReason } from 'modules/templateEditor/common';
import { MessageType } from './GraphQLMessageTypes';
import { DataWithGraphQLTypeName, GRAPHQL_TYPENAMES } from './GraphQLTypeNameData';
import { WhatsAppSender } from 'modules/sendingPlatforms/types/whatsAppSenders';

// Types from Iterable IteraQLApiSpec.yaml
export type TemplateResponse = NoTemplateFound | TemplateAndContent | CorruptedDataFields;

export type CorruptedDataFields = {
  templateId: number;
  contentId: number;
  messageTypeId: number;
  creatorUserId: string;
  updatedBy: string;
};

export type NoTemplateFound = {
  id: number;
};

// Used primarily in template index page and for fetching locale count in template editor
export type Template = {
  id: number;
  name: string;
  messageMedium: MessageMedium;
  createdAt: OffsetDateTime;
  createdByUserId: string;
  updatedAt: OffsetDateTime;
  updatedByUserId: string;
  locales: Locale[];
  contentPreview: string;
  contentId: number;
};

export type TemplateAndContent = TemplateAndContentCommonFields & {
  localeContents?: MediumContent[];
  primaryContent?: MediumContent;
};

export type TemplatesAndContents = TemplateAndContent[];

export type TemplateAndContentCommonFields = {
  templateId: number;
  cacheDataFeed: boolean;
  createdAt?: string;
  creatorUser: User;
  dataFeedIds: number[];
  isAssociatedWithActiveExperiment: boolean;
  jsonTestData?: JsonTestData;
  mergeDataFeedContext: boolean;
  messageType: MessageType;
  name: string;
  referenceDataFeedsByAlias: boolean;
  shortenLinks: boolean;
  templateType?: TemplateType;
  updatedAt?: string;
  updatedByUser: User;
};

export type JsonTestData = {
  dataFeedJson: JSON;
  userJson: JSON;
  eventJson: JSON;
};

export type User = {
  id: string;
  fullName: string;
  avatarUrl?: string;
};

export type MediumContent =
  | EmailContentDefinition
  | InAppContentDefinition
  | SMSContentDefinition
  | PushContentDefinition
  | WebPushContentDefinition
  | EmbeddedContentDefinition
  | WhatsAppContentDefinition;

export type MediumContentArgs =
  | EmailContentArgs
  | InAppContentArgs
  | PushContentArgs
  | SMSContentArgs
  | WebPushContentArgs
  | EmbeddedContentArgs
  | WhatsAppContentArgs;

export type MediumContentArgsInput =
  | EmailContentArgsInput
  | InAppContentArgsInput
  | PushContentArgsInput
  | SMSContentArgsInput
  | WebPushContentArgsInput
  | EmbeddedContentArgsInput
  | WhatsAppContentArgsInput;

export type EmailContentDefinition = {
  ampHtml?: string;
  bccEmails: string[];
  beeBuilderMetadata?: string;
  ccEmails: string[];
  emailContentId?: number;
  fromEmail: string;
  fromName: string;
  googleAnalyticsCampaignName?: string;
  html: string;
  htmlEditor?: HtmlEditor;
  inlineCss?: boolean;
  linkParams?: LinkParam[];
  locale?: TemplateLocale;
  payload?: string;
  plainText: string;
  preheaderText: string;
  replyTo?: string;
  subject: string;
};

export type EmailContentArgs = {
  ampHtml?: string;
  bccEmails: string[];
  beeBuilderMetadata?: string;
  ccEmails: string[];
  emailContentId?: number;
  fromEmail: string;
  fromName: string;
  googleAnalyticsCampaignName?: string;
  html: string;
  htmlEditor?: HtmlEditor;
  inlineCss?: boolean;
  linkParams?: LinkParam[];
  locale?: TemplateLocale;
  payload?: string;
  plainText: string;
  preheaderText: string;
  replyTo?: string;
  subject: string;
};

export type EmailContentArgsInput = {
  ampHtml?: string;
  bccEmails: string[];
  beeBuilderMetadata?: string;
  ccEmails: string[];
  emailContentId?: number;
  fromEmail: string;
  fromName: string;
  googleAnalyticsCampaignName?: string;
  html: string;
  htmlEditor?: HtmlEditor;
  inlineCss?: boolean;
  linkParams?: LinkParam[];
  locale?: TemplateLocaleInput;
  payload?: string;
  plainText: string;
  preheaderText: string;
  replyTo?: string;
  subject: string;
};

export type RokuButtonContent = {
  buttonTextColorFocused: string;
  buttonColorFocused: string;
  buttonTextColorUnFocused: string;
  buttonDeeplink: string;
  buttonColorUnFocused: string;
  buttonText: string;
  buttonBorderColor: string;
};

export type OttPayload = {
  backgroundColor: string;
  dialogText: string;
  dialogHeader: string;
  fhdImage: string;
  dialogBodyFont: string;
  dialogButtonFont: string;
  dialogHeaderFont: string;
  buttons: RokuButtonContent[];
};

export type InAppContentDefinition = {
  beeBuilderMetadata?: string | null;
  expirationDateTime?: string | null;
  expirationDuration?: string | null;
  html: string;
  htmlEditor?: HtmlEditor;
  inAppContentId?: number;
  inAppDisplaySettings: InAppDisplaySettingsDefinition;
  inboxMetadata?: InboxMetadataDefinition;
  locale?: TemplateLocale | null;
  messageFormat: MESSAGE_FORMAT | null;
  payload?: string | null;
  ottPayload?: OttPayload;
  saveToInbox: boolean;
  trigger: InAppTriggerDefinition;
  webInAppDisplaySettings: WebInAppDisplaySettingsDefinition;
  destinations: InAppDestination[];
};

export type InAppContentArgs = {
  beeBuilderMetadata?: string;
  expirationDateTime?: string;
  expirationDuration?: string;
  html: string;
  htmlEditor?: HtmlEditor;
  inAppContentId?: number;
  inAppDisplaySettings: InAppDisplaySettingsDefinition;
  inboxMetadata?: InboxMetadataDefinition;
  locale?: TemplateLocale;
  messageFormat: MESSAGE_FORMAT;
  payload?: string;
  saveToInbox: boolean;
  trigger: InAppTriggerDefinition;
  webInAppDisplaySettings: WebInAppDisplaySettingsDefinition;
};

export type InAppContentArgsInput = {
  beeBuilderMetadata?: string;
  expirationDateTime?: string;
  expirationDuration?: string;
  html: string;
  htmlEditor?: HtmlEditor;
  inAppContentId?: number;
  inAppDisplaySettings: InAppDisplaySettingsDefinition;
  inboxMetadata?: InboxMetadataDefinition;
  locale?: TemplateLocaleInput;
  messageFormat?: MESSAGE_FORMAT;
  payload?: string;
  ottPayload?: OttPayload;
  saveToInbox: boolean;
  trigger: InAppTriggerDefinition;
  webInAppDisplaySettings: WebInAppDisplaySettingsDefinition;
};

export type PushContentDefinition = {
  actionButtons?: PushActionButton[];
  attachmentUrlAndroid?: string;
  attachmentUrlIos?: string;
  badge?: string;
  category?: string;
  contentAvailable: boolean;
  deeplink?: DeeplinkURI;
  defaultAction?: DefaultActionType;
  interruptionLevel?: PushInterruptionLevel;
  locale?: TemplateLocale;
  message?: string;
  payload?: string;
  pushContentId?: number;
  relevanceScore?: number;
  scoreId?: number;
  sound?: string;
  title?: string;
  isSilentPush: boolean;
};

export type PushContentArgs = {
  actionButtons?: PushActionButton[];
  attachmentUrlAndroid?: string;
  attachmentUrlIos?: string;
  badge?: string;
  category?: string;
  contentAvailable: boolean;
  deeplink?: DeeplinkURI;
  defaultAction?: DefaultActionType;
  interruptionLevel?: PushInterruptionLevel;
  locale?: TemplateLocale;
  message: string;
  payload?: string;
  relevanceScore?: number;
  scoreId?: number;
  sound?: string;
  title?: string;
};

export type PushContentArgsInput = {
  actionButtons?: PushActionButton[];
  attachmentUrlAndroid?: string;
  attachmentUrlIos?: string;
  badge?: string;
  category?: string;
  contentAvailable: boolean;
  deeplink?: DeeplinkURI;
  defaultAction?: DefaultActionType;
  interruptionLevel?: PushInterruptionLevel;
  locale?: TemplateLocale;
  message: string;
  payload?: string;
  relevanceScore?: number;
  scoreId?: number;
  sound?: string;
  title?: string;
  isSilentPush: boolean;
};

export type SMSContentDefinition = {
  googleAnalyticsCampaignName?: string;
  imageUrl?: string;
  linkParams?: LinkParam[];
  locale?: TemplateLocale;
  message?: string;
  optOutDisclaimer: SMSOptOutDisclaimer;
  verifiedContactCardId?: number;
  smsContentId?: number;
  smsSender: SMSSender;
  trackingDomain?: string;
};

export enum WhatsAppTemplateCategoryDefinition {
  Authentication = 'Authentication',
  Marketing = 'Marketing',
  Service = 'Service',
  Utility = 'Utility',
}

export enum MetaApprovalStatusDefinition {
  Approved = 'Approved',
  Pending = 'Pending',
  NotSubmitted = 'NotSubmitted',
  Rejected = 'Rejected',
  InAppeal = 'InAppeal',
  PendingDeletion = 'PendingDeletion',
  Deleted = 'Deleted',
  Reinstated = 'Reinstated',
  Flagged = 'Flagged',
  FirstPaused = 'FirstPaused',
  SecondPaused = 'SecondPaused',
  Disabled = 'Disabled',
  Unknown = 'Unknown',
}

/** In order to match designs, we need to add a "Paused" status under which
 * the FirstPaused and SecondPaused statuses will be nested in the template
 * index filters. */
export enum PseudoApprovalStatusDefinition {
  Paused = 'Paused',
}

export enum MetaQualityRating {
  High = 'High',
  Low = 'Low',
  Medium = 'Medium',
  Unknown = 'Unknown',
}

export enum TemplateTypeDefinition {
  BusinessInitiated = 'BusinessInitiated',
  UserInitiated = 'UserInitiated',
}

export type TextHeader = { text?: string };

export type ImageHeader = { mediaUrl?: string };

export type WhatsAppHeaderDefinition = ImageHeader | TextHeader;

type GraphQLTextHeader = DataWithGraphQLTypeName<GRAPHQL_TYPENAMES.TEXT_HEADER, TextHeader>;

type GraphQLImageHeader = DataWithGraphQLTypeName<GRAPHQL_TYPENAMES.IMAGE_HEADER, ImageHeader>;

export type GraphQLWhatsAppHeader = GraphQLImageHeader | GraphQLTextHeader;

type WhatsAppPlaceholderDefinition = {
  value?: string;
  example?: string;
};

export type UrlButton = DataWithGraphQLTypeName<
  GRAPHQL_TYPENAMES.URL_BUTTON,
  {
    title?: string;
    buttonText?: string;
    url?: string;
  }
>;

type CopyCodeButton = {
  title?: string;
  buttonText?: string;
  code?: string;
};

type FlowButton = {
  title?: string;
  buttonText?: string;
  flowToken?: string;
  data?: {
    key?: string;
    value?: string;
  }[];
};

export type CallButton = DataWithGraphQLTypeName<
  GRAPHQL_TYPENAMES.CALL_BUTTON,
  {
    title?: string;
    buttonText?: string;
    phoneNumber?: string;
  }
>;

export type QuickReplyButton = DataWithGraphQLTypeName<
  GRAPHQL_TYPENAMES.QUICK_REPLY_BUTTON,
  {
    title?: string;
    buttonText?: string;
  }
>;

export type WhatsAppButtonDefinition = UrlButton | QuickReplyButton | CallButton;

export enum WhatsAppTemplateRejectionReason {
  ABUSIVE_CONTENT = 'ABUSIVE_CONTENT',
  INCORRECT_CATEGORY = 'INCORRECT_CATEGORY',
  INVALID_FORMAT = 'INVALID_FORMAT',
  NONE = 'NONE',
  SCAM = 'SCAM',
  UNKNOWN = 'UNKNOWN',
}

export type WhatsAppContentDefinition = {
  id?: number;
  templateId: number;
  creatorUserId: string;
  scoreId?: number;
  locale?: TemplateLocale;
  parentId?: number;
  name?: string;
  category: WhatsAppTemplateCategoryDefinition;
  previousCategory?: WhatsAppTemplateCategoryDefinition;
  header?: WhatsAppHeaderDefinition;
  message?: string;
  placeholders: WhatsAppPlaceholderDefinition[];
  buttons?: WhatsAppButtonDefinition[];
  footer?: string;
  metaApprovalStatus?: MetaApprovalStatusDefinition;
  whatsAppSender?: WhatsAppSender;
  whatsAppSenderId?: number;
  whatsAppContentId?: number;
  qualityRating?: MetaQualityRating;
  rejectionReason?: WhatsAppTemplateRejectionReason;
  /** short links */
  googleAnalyticsCampaignName?: string;
  linkParams?: LinkParam[];
  trackingDomain?: string;
};

export type WhatsAppContentArgs = {
  locale?: TemplateLocale;
  scoreId?: number;
  parentId?: number;
  name?: string;
  category: WhatsAppTemplateCategoryDefinition;
  header?: WhatsAppHeaderDefinition;
  message: string;
  placeholders: WhatsAppPlaceholderDefinition[];
  buttons?: WhatsAppButtonDefinition[];
  metaApprovalStatus?: MetaApprovalStatusDefinition;
  whatsAppSender?: WhatsAppSender;
  whatsAppSenderId?: number;
  /** short links */
  googleAnalyticsCampaignName?: string;
  linkParams?: LinkParam[];
  trackingDomain?: string;
};

export type WhatsAppContentArgsInput = {
  whatsAppContentId?: number;
  locale?: TemplateLocale;
  scoreId?: number;
  parentId?: number;
  name?: string;
  category: WhatsAppTemplateCategoryDefinition;
  header?: WhatsAppHeaderDefinition;
  message: string;
  footer?: string;
  placeholders: WhatsAppPlaceholderDefinition[];
  buttons?: WhatsAppButtonDefinition[];
  metaApprovalStatus?: MetaApprovalStatusDefinition;
  whatsAppSenderId?: number;
};

export type WhatsAppTemplateBodyDefinitionInput = {
  text: string;
  examples: string[];
};

export type StructureDefinitionInput = {
  header?: WhatsAppHeaderDefinition;
  body: WhatsAppTemplateBodyDefinitionInput;
  footer?: string;
  buttons?: WhatsAppButtonDefinition[];
};

export type SMSContentArgs = {
  googleAnalyticsCampaignName?: string;
  imageUrl?: string;
  linkParams: LinkParam[];
  locale?: TemplateLocale;
  message: string;
  smsContentId?: number;
  smsSenderId: number;
  trackingDomain?: string;
};

export type SMSContentArgsInput = {
  googleAnalyticsCampaignName?: string;
  imageUrl?: string;
  linkParams?: LinkParamInput[];
  locale?: TemplateLocaleInput;
  message: string;
  smsContentId?: number;
  smsSenderId: number;
  trackingDomain?: string;
  verifiedContactCardId?: number;
};

export type WebPushContentDefinition = {
  clickAction?: string;
  icon?: string;
  locale?: TemplateLocale;
  message?: string;
  title?: string;
  webPushContentId?: number;
};

export type WebPushContentArgs = {
  clickAction?: string;
  icon?: string;
  locale?: TemplateLocale;
  message?: string;
  scoreId?: number;
  title?: string;
  webPushContentId?: number;
};

export type WebPushContentArgsInput = {
  clickAction?: string;
  icon?: string;
  locale?: TemplateLocaleInput;
  message?: string;
  scoreId?: number;
  title?: string;
  webPushContentId?: number;
};

export type EmbeddedAction = {
  type: string;
  data: string;
};

export type EmbeddedContentDefinition = {
  body?: string;
  elements?: Elements;
  embeddedContentId?: number;
  listId?: number;
  locale?: TemplateLocale;
  payload?: string;
  placementId?: number;
  title?: string;
};

export type EmbeddedContentArgs = {
  body?: string;
  elements?: Elements;
  listId?: number;
  locale?: TemplateLocale;
  payload?: string;
  placementId?: number;
  title?: string;
};

export type EmbeddedContentArgsInput = {
  body?: string;
  elements?: Elements;
  listId?: number;
  locale?: TemplateLocale;
  payload?: string;
  placementId?: number;
  title?: string;
};

type Elements = {
  buttons?: Button[];
  defaultAction?: DefaultActionType;
  mediaUrl?: string;
  text?: Text[];
};

export type Button = {
  id: string;
  title: string;
  action: EmbeddedAction;
};

export type Image = {
  id: string;
  url?: string;
};

export type Text = {
  id: string;
  label?: string;
  text?: string;
};

type Disable = {
  _: boolean;
  message: string;
  __typename: string;
};

type OnEverySMS = {
  message: string;
  __typename: string;
};

type OnFirstSMS = {
  message: string;
  __typename: string;
};

export type SMSOptOutDisclaimer = Disable | OnEverySMS | OnFirstSMS;

export type LinkParam = {
  key: string;
  value: string;
};

export type SMSSender = {
  id: number;
  phoneNumber: string;
  name: string;
  keyword?: string;
  type: string;
};

export type TemplateLocale = {
  name: string;
};

type LinkParamInput = {
  key: string;
  value: string;
};

export type TemplateLocaleInput = {
  name: string;
};

export type TemplateLocalesResponse = {
  templateId?: number;
  defaultLocale?: Locale;
  failures?: TemplateLocalesFailure;
};

export type TemplateLocalesFailure = LocaleNotFound;

export type UpsertTemplateResult = {
  templateId?: number;
  failure?: UpsertTemplateFailure;
};

export type WhatsAppTemplateSubmissionError = {
  message: string;
};

export type WhatsAppTemplateSubmissionResponse = {
  name: string;
  language: string;
  status: string;
};

export type SubmitTemplateResult =
  | WhatsAppTemplateSubmissionResponse
  | WhatsAppTemplateSubmissionError;

export type UpsertTemplateFailure =
  | InvalidTemplateContents
  | InvalidTemplateName
  | TemplateNotFound;

export type FolderFailure = {
  failure: AddContentFailure;
};

type AddContentFailure = LocationNotFound; // note: eventually this will be a union

type LocationNotFound = {
  folderId: number;
};

export type InvalidTemplateContents = {
  errors: InvalidTemplateContent[];
};

export type InvalidTemplateContent = {
  reasons: GqlInvalidContentReason[];
  content: MediumContentArgs;
};

export type InvalidContentReason = InvalidSnippetParams | MissingSnippets | Unrenderable;

export type InvalidSnippetParams = {
  snippetsWithParams: string[];
};

export type InvalidWhatsAppButtonType = {
  index: number;
};

export type MissingSnippets = {
  snippets: string[];
};

export enum UnrenderableFieldName {
  smsMessage = 'SMS message',
  smsMessageDataFeed = 'SMS message data feed',
}

export type Unrenderable = {
  fieldName: string;
  msg: string;
};

export type UnrenderableWithIndex = {
  fieldName: string;
  msg: string;
  index: number;
};

export type InvalidTemplateName = {
  reason: string;
};

export type InvalidTemplateLocale = {
  reason: string;
};

export type TemplateNotFound = {
  templateId: number;
};

export type LocaleNotFound = {
  locale: TemplateLocale;
};

export type InvalidContentBooleanType = {
  _: boolean;
};

export type InvalidContentFieldNameType = {
  fieldName: string;
};

export type InvalidContentIndexType = {
  index?: number;
};

export type InvalidContentFieldNameIndexType = {
  fieldName: string;
  index?: number;
};

export type FieldInvalidType = {
  errorMsg: string;
};

export type InvalidAttribute = InvalidContentFieldNameType & { tagName: string };
export type IllegalScript = InvalidContentFieldNameType;
export type InvalidEmail = InvalidContentFieldNameType;
export type JSInAnchorTag = InvalidContentFieldNameType;
export type JSInIframeTag = InvalidContentFieldNameType;
export type InvalidHandlebarCount = InvalidContentFieldNameIndexType;
export type InvalidHandlebarComplexity = InvalidContentFieldNameIndexType;
export type InvalidSnippetUsage = InvalidContentFieldNameIndexType;
export type EmojiInButtonText = InvalidContentIndexType;
export type WhatsAppEmojiLimitExceeded = InvalidContentBooleanType;
export type EmptyContent = InvalidContentFieldNameType;
export type EmptyWhatsAppButtonText = InvalidContentIndexType;
export type EmptyWhatsAppButtonPhoneNumber = InvalidContentIndexType;
export type EmptyWhatsAppButtonUrl = InvalidContentIndexType;
export type EmptyWhatsAppMessage = InvalidContentBooleanType;
export type InvalidSubject = InvalidContentBooleanType;
export type InvalidWhatsAppButtonUrlFormat = InvalidWhatsAppButtonType;
export type InvalidWhatsAppButtonUrlParams = InvalidWhatsAppButtonType;
export type InvalidWhatsAppFooter = InvalidContentBooleanType;
export type InvalidUrlFormat = InvalidContentBooleanType;
export type InvalidImageSize = InvalidContentBooleanType;
export type InvalidImageExtension = InvalidContentBooleanType;
export type InvalidExpiration = InvalidContentBooleanType;
export type MissingUnsubscribeUrl = InvalidContentBooleanType;
export type AmbiguousExpiration = InvalidContentBooleanType;
export type InvalidMessageTypeId = InvalidContentBooleanType;
export type PreheaderTooLong = InvalidContentBooleanType;
export type MissingLink = InvalidContentBooleanType;
export type WhatsAppCategoryUpdateAttemptedAfterSubmission = InvalidContentBooleanType;

export type AnchorTagsWithJS = FieldInvalidType;
export type HasCycle = FieldInvalidType;
export type HasScriptTag = FieldInvalidType;
export type IframeTagWithJS = FieldInvalidType;
export type IllegalCharacters = FieldInvalidType;
export type NotRenderable = FieldInvalidType;

export enum ActionIconType {
  systemImage = 'systemImage',
  templateImage = 'templateImage',
}

type ActionIcon = {
  iconType: ActionIconType;
  imageName: string;
};

type PushAction = {
  type: string;
  data: string;
};

export enum PushActionButtonType {
  default = 'default',
  destructive = 'destructive',
  textInput = 'textInput',
}

export enum WhatsAppButtonType {
  quickReplyButton = 'quickReplyButton',
  urlButton = 'urlButton',
  callButton = 'callButton',
}

export type PushActionButton = {
  identifier: string;
  title: string;
  buttonType: PushActionButtonType;
  action: PushAction;
  openApp: boolean;
  requiresUnlock: boolean;
  inputPlaceholder: string;
  inputTitle: string;
  actionIcon: ActionIcon;
};

/**
 * PushActionButtonForFrontend used for only in RadioButtonGroup.tsx
 * to handle 4 different actions: openApp, trackOnly, openUrl, and customAction logics.
 * Backend doesn't have isCustomAction: boolean; but because of how BE is determining actions,
 * we need this extra field to handle UI.
 */
export type PushActionButtonForFrontend = {
  identifier: string;
  title: string;
  buttonType: PushActionButtonType;
  action: PushActionForFrontend;
  openApp: boolean;
  requiresUnlock: boolean;
  inputPlaceholder: string;
  inputTitle: string;
  actionIcon: ActionIcon;
};

/**
 * Again, this is only used for frontend.
 * When sending data, we should NOT use PushActionButtonForFrontend and PushActionForFrontend.
 * Use PushActionButton and PushAction instead.
 */
export type PushActionForFrontend = {
  isCustomAction: boolean;
} & PushAction;

export type DefaultActionType = {
  type: string;
  data: string;
};

type DeeplinkURI = {
  ios: string;
  android: string;
};

export type InAppDisplaySettingsDefinition = {
  // Top-padding percentage 0-100, or autoexpand if None
  top: number | null;
  // Bottom-padding percentage 0-100, or autoexpand if None
  bottom: number | null;
  // Left-padding percentage 0-100, or autoexpand if None
  left: number | null;
  // Right-padding percentage 0-100, or autoexpand if None
  right: number | null;
  bgColor?: BgColor | null;
  shouldAnimate?: boolean | null;
};

export type BgColor = {
  alpha: number;
  hex: string;
};

export type InboxMetadataDefinition = {
  title?: string;
  subtitle?: string;
  icon?: string;
};

export enum InAppTriggerDefinition {
  Immediate = 'Immediate',
  Never = 'Never',
}

export enum WebInAppDisplaySettingsDefinition {
  BottomRight = 'BottomRight',
  Center = 'Center',
  Full = 'Full',
  TopRight = 'TopRight',
}

enum PushInterruptionLevel {
  Active = 'Active',
  Critical = 'Critical',
  Passive = 'Passive',
  TimeSensitive = 'TimeSensitive',
}

export enum HtmlEditor {
  Ace = 'Ace',
  Bee = 'Bee',
  Wysiwyg = 'Wysiwyg',
}

export enum MESSAGE_FORMAT {
  Everything = 'Everything',
  Json = 'Json',
  Html = 'Html',
}

export enum Position {
  Bottom = 'Bottom',
  Center = 'Center',
  Full = 'Full',
  Top = 'Top',
}

export enum InAppDestination {
  Mobile = 'Mobile',
  Browser = 'Browser',
  Roku = 'Roku',
}
